import { useInfoPanelDetails } from 'gcs-common/clients/api/entities/channels/channel-api';
import { CHANNEL_TYPE } from 'gcs-common/helper/channelTypeHelper';
import USER_TYPE from 'gcs-common/helper/userTypeHelper';
import { getChannelImage, getMainContactId } from 'gcs-common/slices/channel/channelSelectors';
import { getCurrentUserId, getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getIsMobile } from 'gcs-common/slices/init/initSelectors';
import { useSelector } from 'react-redux';
import type { components } from '../../../types/openapi';
import ProfileImage, { ProfileImageSize } from './ListComponents/ProfileImage';
import PanelHeader from './PanelHeader';

type InfoPanelHeaderProps = {
  userIds: string[];
  channelId: string;
  isCurrentUserAgent: boolean;
  onClose: VoidFunction;
};

const MAX_NUMBER_OF_IMAGES_TO_SHOW = 2;

const InfoPanelHeader = (props: InfoPanelHeaderProps) => {
  const { onClose } = props;
  const isMobile = useSelector(getIsMobile);
  const currentUserId = useSelector(getCurrentUserId);
  const currentUserIsAgent = useSelector(getIsAgent);
  const { data } = useInfoPanelDetails(props.channelId);
  const usersToDisplay = data?.users ?? [];

  const numberOfImagesToDipslay = Math.min(MAX_NUMBER_OF_IMAGES_TO_SHOW, usersToDisplay.length);
  const userImagesToDisplay = usersToDisplay
    .sort((a, b) => {
      if (!a.imageUrl) return 1;
      if (!b.imageUrl) return -1;
      return a.friendlyName.localeCompare(b.friendlyName);
    })
    .slice(0, numberOfImagesToDipslay)
    .map((user) => user.imageUrl);

  const userFromCustomer = usersToDisplay.find((user) => user.userType === 'CRAFTSMAN');
  const customerName = userFromCustomer?.customerFriendlyName;
  const customerNo = userFromCustomer?.customerNo;
  const chatTitle = data?.channelType === 'sales_space' ? 'Teamchat' : 'Direkter Chat';
  const isAgentInternalChat = !usersToDisplay.some((user) => user.userType === 'CRAFTSMAN');
  const mainContactId = useSelector(getMainContactId(props.channelId));
  const channelImageUrl = useSelector(getChannelImage(props.channelId));

  const isRounded = !(data?.channelType === CHANNEL_TYPE.SALES_SPACE) || props.isCurrentUserAgent;

  const getSalesSpaceChannelName = (
    isCurrentUserAgent: boolean,
    users: components['schemas']['UserWithHouseOrCustomerDTO'][],
    communicationName: string,
  ) => {
    const craftsman = users.find((u) => u.userType === USER_TYPE.CRAFTSMAN);
    if (isCurrentUserAgent) {
      return `${data?.channelName ? data.channelName : craftsman ? craftsman.friendlyName : 'Gelöschter Handwerker'} - ${communicationName}`;
    }
    const mainContact = users.find((u) => u.id === mainContactId);
    if (mainContact) {
      return mainContact.friendlyName;
    }
    return communicationName;
  };

  const getOtherChannelName = (currentUserId: string, users: components['schemas']['UserWithHouseOrCustomerDTO'][]) => {
    return users
      .filter((u) => u.id !== currentUserId)
      .sort((a, b) => {
        if (a.userType === USER_TYPE.CRAFTSMAN && b.userType === USER_TYPE.AGENT) return -1;
        if (a.userType === USER_TYPE.AGENT && b.userType === USER_TYPE.CRAFTSMAN) return 1;
        return a.friendlyName.localeCompare(b.friendlyName);
      })
      .map((u) => u.friendlyName)
      .join(', ');
  };

  const getChannelName = () => {
    if (data?.channelType === CHANNEL_TYPE.SALES_SPACE) {
      return getSalesSpaceChannelName(currentUserIsAgent, data.users, data.friendlyName || '');
    }
    return getOtherChannelName(currentUserId, data?.users || []);
  };

  const channelName = getChannelName();

  return (
    <div className="flex flex-col">
      <PanelHeader
        title={chatTitle}
        onBack={isMobile ? onClose : undefined}
        onClose={!isMobile ? onClose : undefined}
      />
      <div className="flex flex-col items-center gap-4 p-4">
        {data?.channelType === CHANNEL_TYPE.SALES_SPACE ? (
          <div className="self-center">
            <ProfileImage imageUrl={channelImageUrl} size={ProfileImageSize.LARGE} isRounded={isRounded} />
          </div>
        ) : userImagesToDisplay.length === 1 || !currentUserIsAgent ? (
          <div className="self-center">
            <ProfileImage imageUrl={userImagesToDisplay[0]} size={ProfileImageSize.LARGE} isRounded={isRounded} />
          </div>
        ) : (
          <div className=" relative h-24 w-24">
            <div className="absolute top-0 right-0 h-[68px] w-[68px]">
              <ProfileImage imageUrl={userImagesToDisplay[0]} size={ProfileImageSize.MEDIUM} isRounded={true} />
            </div>
            <div className="absolute bottom-0 left-0 h-[68px] w-[68px]">
              <ProfileImage imageUrl={userImagesToDisplay[1]} size={ProfileImageSize.MEDIUM} isRounded={true} />
            </div>
          </div>
        )}
        <div className="justify-items-center">
          <div className="text-center text-xl">{channelName}</div>
          {chatTitle === 'Teamchat' && <div className="pb-1 text-xl"> Innendienst </div>}{' '}
          {props.isCurrentUserAgent && !isAgentInternalChat && (
            <div className="text-neutral-11 text-sm"> {`${customerName} ∙ ${customerNo}`} </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoPanelHeader;
